// Building collision: solid walls (push-out), landable roofs (ray vs real roof
// tris), climbable facades. Data = per-tile col_{tx}_{ty}.bin (COL1, game/colbin.js) from the bake
// (footprints from the swissBUILDINGS3D Floor layer, roof tris from Roof).
// ONE collider, two users: the client's Player resolves its body against it
// (main.js), and the game server's Sim rejects a reported move whose centre
// chord walks through a wall (`crosses`; game/buildcol.js holds the instance,
// APP_BAKER §5 "Server").
'use strict';

// Broadphase cell size. Collision tiles are 1024 m, so a 3x3 tile scan is
// effectively "every building in the village" — several hundred bbox tests per
// resolve, and resolve now runs up to ~13x per frame. A 32 m grid cuts that to
// a handful.
const CELL = 32;

class BuildingCollision {
  constructor(tileM) {
    this.tileM = tileM || 1024;
    this.tiles = new Map();   // "tx_ty" -> [{fp, y0, y1, roof, bbox}]
    this.extras = [];         // synthetic colliders (addExtra; the gates' fixtures) — never evicted
    this.grid = new Map();    // cx -> Map(cz -> [item]) broadphase (no string keys per query)
    this._stamp = 0;          // visit stamp, dedups items spanning several cells
    this._nearBuf = [];       // _near's result, reused (read before the next call)
    this.gen = 0;             // bumps on every tile / extra change (grass's scatter cache keys on it)
  }

  // Broadphase bbox of one collider. Stand-only surfaces (bridge decks, a
  // roof-only addExtra, and since bake rev 51 the `gallery`/`canopy` structure
  // classes — a covered bridge you walk THROUGH) carry no footprint, so the
  // roof triangles have to be included or the bbox comes out INVERTED and
  // _index()'s loops never run: the collider is silently never indexed and the
  // surface is not there. addExtra() always did this; addTile() did not, which
  // was invisible only because nothing had yet shipped a roof-only col entry
  // in a TILE file. One helper, so the two paths cannot drift again.
  _index(items) {
    for (const b of items) {
      const [x0, z0, x1, z1] = b.bbox;
      for (let cz = Math.floor(z0 / CELL); cz <= Math.floor(z1 / CELL); cz++) {
        for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
          let col = this.grid.get(cx);
          if (!col) this.grid.set(cx, (col = new Map()));
          const cell = col.get(cz);
          if (cell) cell.push(b); else col.set(cz, [b]);
        }
      }
    }
  }

  // the cells of `items` lose them (removeTile). It re-indexed EVERY resident
  // tile per eviction (a long task per tile left behind on a walk); the cells
  // keep the other items in the order a full re-index would give them
  // (insertion order of the tiles, which Map iteration also is)
  _unindex(items) {
    const gone = new Set(items);
    for (const b of items) {
      const [x0, z0, x1, z1] = b.bbox;
      for (let cz = Math.floor(z0 / CELL); cz <= Math.floor(z1 / CELL); cz++) {
        for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
          const col = this.grid.get(cx);
          const cell = col && col.get(cz);
          if (!cell) continue;
          const keep = cell.filter((c) => !gone.has(c));
          if (keep.length) col.set(cz, keep);
          else { col.delete(cz); if (!col.size) this.grid.delete(cx); }
        }
      }
    }
  }

  addExtra(b) {
    this.extras.push({ ...b, bbox: bboxOf(b, true) });
    this.gen++;
  }

  addTile(key, data) {
    const items = (data.buildings || []).map((b0) => {
      const b = { ...b0, fp: (b0.fp || []).map(cleanRing).filter((r) => r.length >= 3) };
      return { ...b, bbox: bboxOf(b, false) };
    });
    const prev = this.tiles.get(key);
    if (prev) this._unindex(prev);    // a re-add replaces (buildings.js never re-adds a resident key)
    this.tiles.set(key, items);
    this._index(items);
    this.gen++;
  }

  removeTile(key) {
    const items = this.tiles.get(key);
    if (items && this.tiles.delete(key)) { this._unindex(items); this.gen++; }
  }

  // cheap point test used by decoration scatter (grass) to stay out of houses
  insideFootprint(x, z) {
    const near = this._near(x, z), n = near.length, ex = this.extras;
    for (let i = 0, m = n + ex.length; i < m; i++) {
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      if (x < bb[0] || x > bb[2] || z < bb[1] || z > bb[3]) continue;
      if (inAnyRing(x, z, b.fp)) return true;
    }
    return false;
  }

  // The building (tile item) whose footprint contains (x, z), else the
  // nearest one whose outline lies within `tol` m (a depth hit on a facade
  // lands centimetres outside its own ring), else null. Stand-only surfaces
  // (no footprint: bridge decks) and the extras never answer. Items carry
  // `uuid` (the buildings/registry.json key) on bakes since TRAILS-W.
  // Inspect card only (one call per trigger), not a per-frame path.
  buildingAt(x, z, tol = 0) {
    const near = this._near(x, z);
    let best = null, bd = tol;
    for (let i = 0; i < near.length; i++) {
      const b = near[i];
      if (!b.fp || !b.fp.length) continue;
      const bb = b.bbox;
      if (x < bb[0] - tol || x > bb[2] + tol || z < bb[1] - tol || z > bb[3] + tol) continue;
      if (inAnyRing(x, z, b.fp)) return b;
      if (tol > 0) {
        for (const ring of b.fp) {
          const d = distToRing(x, z, ring);
          if (d <= bd) { bd = d; best = b; }
        }
      }
    }
    return best;
  }

  // Solid at a POINT — footprint plus the building's own height band. The 2D
  // test above reports the whole column solid, sky included, which is right
  // for a body standing on the ground and wrong for probing where a rifle
  // barrel is (you can hold one over a low roof). Used by the muzzle probe.
  solidAt(x, y, z) {
    const near = this._near(x, z), n = near.length, ex = this.extras;
    for (let i = 0, m = n + ex.length; i < m; i++) {
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      if (x < bb[0] || x > bb[2] || z < bb[1] || z > bb[3]) continue;
      if (y < b.y0 || y > b.y1) continue;
      if (inAnyRing(x, z, b.fp)) return true;
    }
    return false;
  }

  // Broadphase: the grid items of the 3x3 cells around (x,z), each once, in
  // cell order. The callers then walk this.extras after them — the same
  // candidates in the same order the old generator yielded. It ran several
  // times a frame (muzzle probe, resolve) and cost an iterator result per
  // candidate, extras included: MBs of garbage a frame. The array is reused,
  // so every caller reads it through before the next query.
  _near(x, z) {
    const out = this._nearBuf;
    out.length = 0;
    const cx = Math.floor(x / CELL), cz = Math.floor(z / CELL);
    const stamp = ++this._stamp;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const col = this.grid.get(cx + dx);
        const cell = col && col.get(cz + dz);
        if (!cell) continue;
        for (let i = 0; i < cell.length; i++) {
          const b = cell[i];
          if (b._vs === stamp) continue;   // spans several cells
          b._vs = stamp;
          out.push(b);
        }
      }
    }
    return out;
  }

  // Resolve a body at (x, feetY, z) with radius r.
  // Returns { push:{dx,dz,depth}|null, ground:number|null }.
  //
  // `push` is a DISPLACEMENT, not an absolute destination, and it separates the
  // body by exactly its overlap — never past the wall. The old absolute snap
  // ejected a full body radius BEYOND the nearest edge, which for the
  // coincident party wall of two abutting buildings (swissBUILDINGS3D stores a
  // terrace as separate records) deposited the body inside the neighbour and
  // ping-ponged between the two forever.
  resolve(x, feetY, z, r = 0.35) {
    let ground = null;
    let ceiling = null;      // lowest stand-only surface overhead (bridge deck)
    let passCeil = null;     // lowest drawn passage ceiling overhead (fp_src 'passage')
    const solids = [];
    const near = this._near(x, z), n = near.length, ex = this.extras;
    for (let i = 0, m = n + ex.length; i < m; i++) {
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      if (x < bb[0] - r || x > bb[2] + r || z < bb[1] - r || z > bb[3] + r) continue;
      if (feetY > b.y1 + 0.5) continue;             // flying well above
      // PASSAGE: the building over a DRAWN passage (bake: buildings.py
      // passage_prisms, `fp_src: 'passage'`) starts at the drawn passage
      // ceiling with the lane under it. A body whose feet are below that
      // underside is UNDER it, never in it: inside its plan the underside is
      // a CEILING (the bridge-deck rule: Player clamps the head at
      // land.ceiling), outside its plan nothing. Read before the head band
      // below, which would take a jump's apex (1.56 m) under a ceiling
      // < 3.06 m for "inside" and shove the body along the passage out of
      // its mouth. DRAWN == FELT: the head stops at the drawn ceiling.
      if (b.fp_src === 'passage' && feetY < b.y0) {
        if (inAnyRing(x, z, b.fp) && !(b.holes && inAnyRing(x, z, b.holes))
          && b.y0 - feetY < 4.0 && (passCeil === null || b.y0 < passCeil)) passCeil = b.y0;
        continue;
      }
      // ...and below the building's own footing it is not there either: a
      // shell is a volume, not a column to -infinity. Without this a tunnel
      // bore passing under a house pushed the walker through the bore wall,
      // and a high addExtra deck became an invisible pillar down to the ground.
      if (b.fp.length && feetY < b.y0 - 1.5) continue;
      // no footprint = stand-only surface (bridge deck): the bbox gate above
      // suffices, the roof tris decide the rest — PER LEVEL, not per extra.
      // A switchback boardwalk's legs overlap in plan half a metre apart, and
      // "the highest tri of the extra" read the upper leg over a body walking
      // the lower one, un-granting the very deck under its feet. Ground is
      // the highest tri inside the step-up window; anything above the feet is
      // a candidate ceiling (both at once is exactly a stacked deck).
      if (!b.fp.length) {
        const span = roofSpanAt(b, x, z, feetY);
        if (span.below !== null && (ground === null || span.below > ground)) {
          ground = span.below;
        }
        if (span.above !== null && span.above - feetY < 4.0
          && (ceiling === null || span.above < ceiling)) {
          ceiling = span.above;
        }
        continue;
      }
      // A courtyard / light-well is NOT solid — `holes` carves it back out.
      const inOuter = inAnyRing(x, z, b.fp);
      const inside = inOuter
        && !(b.holes && inAnyRing(x, z, b.holes));
      // above the eaves: try to stand on the actual roof. Tight tolerance —
      // a generous one lets you wade into the short uphill wall of a slope
      // building and see the hollow interior.
      const roofY = roofHeightAt(b, x, z);
      // b.y1 is the building's APEX — only a legitimate stand height when there
      // is no roof mesh at all. Using it wherever a tri happened to be missing
      // put the stand plane metres above the eave and ejected anyone there.
      const standY = roofY !== null ? roofY
        : ((b.roof && b.roof.length) ? null : b.y1);
      if (standY !== null && (roofY !== null || inside) && feetY >= standY - 0.35) {
        if (ground === null || standY > ground) ground = standY;
        continue;
      }
      solids.push({ b, inside });                    // stand-only never pushes
    }
    // a passage ceiling is reported whether or not a wall is near (the
    // passage's side walls are the building's own footprint, always near)
    if (passCeil !== null && (ceiling === null || passCeil < ceiling)) ceiling = passCeil;
    if (!solids.length) return { push: null, ground, ceiling };
    // An exit that lands the body inside ANOTHER solid is not an exit. The
    // shared party wall of two abutting buildings is simultaneously "outside A"
    // and "inside B", so resolving each footprint in isolation pushed the body
    // A -> B -> A forever, however exact the depth. Exits are therefore tested
    // against the UNION of everything overlapping here.
    const blocked = (cx, cz, self) => solids.some((o) => o.b !== self
      && o.b.fp.some((ring) => pointInRing(cx, cz, ring))
      && !(o.b.holes && o.b.holes.some((ring) => pointInRing(cx, cz, ring))));
    let push = null;
    for (const { b, inside } of solids) {
      const blk = (cx, cz) => blocked(cx, cz, b);
      // SHORTEST way out of this building — its outer rings and, when the body
      // is in the wall of a courtyard building, the courtyard boundary too
      // (stepping into the light-well is a legitimate exit).
      let best = null;
      for (const ring of b.fp) {
        const p = ringPush(x, z, ring, r, blk, inside);
        if (p && (!best || p.depth < best.depth)) best = p;
      }
      if (inOuterOf(b, x, z)) {
        for (const ring of b.holes || []) {
          const p = ringPush(x, z, ring, r, blk, inside);
          if (p && (!best || p.depth < best.depth)) best = p;
        }
      }
      // ...but across buildings the DEEPEST overlap is the urgent one
      if (best && (!push || best.depth > push.depth)) push = best;
    }
    return passCeil === null ? { push, ground } : { push, ground, ceiling };
  }

  // Escape hatch: the nearest genuinely free spot around (x,z).
  // A body can sit in a notch narrower than its own diameter — the union of two
  // wings, or a simplify artifact — where the walls on either side demand
  // opposite pushes that cancel. No single wall normal can free it, so the only
  // way out is to look for open space directly.
  // Radii reach far enough to leave a large hotel/complex from deep inside —
  // only ever walked until the first free hit, and only from the stuck path.
  // A spot behind another wall is not free: the walk there would cross it
  // (crosses — the server rejects that chord). So each radius also tries
  // both ways along the wall (the push's tangent): out of a slit narrower
  // than the body runs along its axis, which the 12 directions miss when
  // the slit is skew to them.
  findFree(x, feetY, z, r = 0.35) {
    const p = this.resolve(x, feetY, z, r).push;
    const l = p ? Math.hypot(p.dx, p.dz) : 0;
    const tx = l > 1e-9 ? -p.dz / l : 0, tz = l > 1e-9 ? p.dx / l : 0;
    const nd = l > 1e-9 ? 14 : 12;
    for (const rad of [0.5, 1.0, 1.6, 2.4, 3.2, 4.8, 7, 10, 14, 20, 28]) {
      for (let a = 0; a < nd; a++) {
        const th = (a / 12) * Math.PI * 2;
        const s = a === 12 ? 1 : -1;
        const ux = a < 12 ? Math.cos(th) : tx * s, uz = a < 12 ? Math.sin(th) : tz * s;
        const ex = x + ux * rad, ez = z + uz * rad;
        if (!this.resolve(ex, feetY, ez, r).push
          && !this.crosses(x, feetY, z, ex, feetY, ez)) return { x: ex, z: ez };
      }
    }
    return null;
  }

  // Server check (APP_BAKER §5 "Server"): did the CENTRE chord (a point,
  // radius 0) from the last valid position walk through a wall? Solid is
  // what resolve() pushes against, at the HIGHER of the two feet heights: a
  // footprint minus its holes, not over the eaves or below the footing, a
  // passage only from its drawn ceiling up, nowhere the feet stand on the
  // roof. Conservative like StructCollider.crosses: a chord that STARTS
  // solid-inside a building is not judged at all — the union is the solid
  // (resolve's exits), and spawn, a late tile and the anti-stuck escape walk
  // out of a block through its party walls — and touching is not crossing.
  // A legit body slides 0.35 m off every wall, so its centre chord never
  // meets one.
  crosses(x0, y0, z0, x1, y1, z1) {
    const yb = Math.max(y0, y1);
    const bx0 = Math.min(x0, x1), bz0 = Math.min(z0, z1);
    const bx1 = Math.max(x0, x1), bz1 = Math.max(z0, z1);
    const near = this._span(bx0, bz0, bx1, bz1), n = near.length, ex = this.extras, m = n + ex.length;
    const L = Math.hypot(x1 - x0, z1 - z0);
    const ux = L > 0 ? (x1 - x0) / L : 0, uz = L > 0 ? (z1 - z0) / L : 0;
    const band = (b) => b.fp.length && yb <= b.y1 + 0.5 && yb >= b.y0 - 1.5
      && !(b.fp_src === 'passage' && yb < b.y0);
    for (let i = 0; i < m; i++) {                    // started inside: not judged
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      if (x0 < bb[0] || x0 > bb[2] || z0 < bb[1] || z0 > bb[3]) continue;
      if (band(b) && solidIn(b, x0, z0, yb)) return false;
    }
    for (let i = 0; i < m; i++) {
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      if (bx1 < bb[0] || bx0 > bb[2] || bz1 < bb[1] || bz0 > bb[3]) continue;
      if (!band(b)) continue;
      if (solidIn(b, x1, z1, yb)) return true;       // ended inside
      for (let k = 0, nr = b.fp.length + (b.holes ? b.holes.length : 0); k < nr; k++) {
        const ring = k < b.fp.length ? b.fp[k] : b.holes[k - b.fp.length];
        for (let p = 0, q = ring.length - 1; p < ring.length; q = p++) {
          const ax = ring[q][0], az = ring[q][1], cx = ring[p][0], cz = ring[p][1];
          const o1 = (cx - ax) * (z0 - az) - (cz - az) * (x0 - ax);
          const o2 = (cx - ax) * (z1 - az) - (cz - az) * (x1 - ax);
          if (!(o1 * o2 < 0)) continue;
          const o3 = (x1 - x0) * (az - z0) - (z1 - z0) * (ax - x0);
          const o4 = (x1 - x0) * (cz - z0) - (z1 - z0) * (cx - x0);
          if (!(o3 * o4 < 0)) continue;
          // a proper crossing: a wall where either side of it is solid (the
          // eaves, a courtyard and a roof the feet stand on are not)
          const t = (o1 / (o1 - o2)) * L;
          const qx = x0 + ux * t, qz = z0 + uz * t;
          if (solidIn(b, qx - ux * CROSS_EPS, qz - uz * CROSS_EPS, yb)
            || solidIn(b, qx + ux * CROSS_EPS, qz + uz * CROSS_EPS, yb)) return true;
        }
      }
    }
    return false;
  }

  // Broadphase over a box: the grid items of every cell it touches, each
  // once (crosses' chord; _near's buffer and stamp, so read it through first)
  _span(x0, z0, x1, z1) {
    const out = this._nearBuf;
    out.length = 0;
    const stamp = ++this._stamp;
    for (let cx = Math.floor(x0 / CELL), cx1 = Math.floor(x1 / CELL); cx <= cx1; cx++) {
      const col = this.grid.get(cx);
      if (!col) continue;
      for (let cz = Math.floor(z0 / CELL), cz1 = Math.floor(z1 / CELL); cz <= cz1; cz++) {
        const cell = col.get(cz);
        if (!cell) continue;
        for (let i = 0; i < cell.length; i++) {
          const b = cell[i];
          if (b._vs === stamp) continue;
          b._vs = stamp;
          out.push(b);
        }
      }
    }
    return out;
  }

  // facade grab: hand near a wall (just outside or inside the footprint edge),
  // below the roofline
  grabWall(x, y, z) {
    const near = this._near(x, z), n = near.length, ex = this.extras;
    for (let i = 0, k = n + ex.length; i < k; i++) {
      const b = i < n ? near[i] : ex[i - n];
      const bb = b.bbox;
      const m = 0.5;
      if (x < bb[0] - m || x > bb[2] + m || z < bb[1] - m || z > bb[3] + m) continue;
      if (y < b.y0 - 0.5 || y > b.y1 + 0.3) continue;
      for (const ring of b.fp) {
        if (distToRing(x, z, ring) < 0.55) return true;
      }
    }
    return false;
  }
}

// ---------------------------------------------------------------- geometry
// Broadphase bbox. `roofAlways` is the extras' rule (unchanged); tiles fold in
// the roof ONLY when there is no footprint at all (`x1 < x0` = no fp point was
// seen), because a roof legitimately OVERHANGS its walls and widening every
// building's bbox by its eaves would change what the broadphase admits for the
// whole village — a different change from the one this exists for.
function bboxOf(b, roofAlways) {
  let x0 = 1e9, z0 = 1e9, x1 = -1e9, z1 = -1e9;
  for (const ring of b.fp || []) {
    for (const [x, z] of ring) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (z < z0) z0 = z; if (z > z1) z1 = z;
    }
  }
  if (roofAlways || x1 < x0) {
    for (const tri of b.roof || []) {
      for (const [x, , z] of tri) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
    }
  }
  return [x0, z0, x1, z1];
}

// vertical ray vs an entry's roof triangles, split at the feet: the highest
// tri at/below the step-up window is the walkable level, the lowest tri above
// the feet is its ceiling. Stand-only extras are multi-level — a switchback
// boardwalk's legs overlap in plan half a metre apart, and "the highest tri
// of the extra" read the upper leg over a body walking the lower one,
// un-granting the very deck under its feet. resolve() seats through this.
function roofSpanAt(b, x, z, feetY) {
  let below = null, above = null;
  for (const tri of b.roof || []) {
    const [p0, p1, p2] = tri;
    const d = (p1[2] - p2[2]) * (p0[0] - p2[0]) + (p2[0] - p1[0]) * (p0[2] - p2[2]);
    if (Math.abs(d) < 1e-9) continue;
    const w0 = ((p1[2] - p2[2]) * (x - p2[0]) + (p2[0] - p1[0]) * (z - p2[2])) / d;
    const w1 = ((p2[2] - p0[2]) * (x - p2[0]) + (p0[0] - p2[0]) * (z - p2[2])) / d;
    const w2 = 1 - w0 - w1;
    if (w0 < -0.02 || w1 < -0.02 || w2 < -0.02) continue;
    const y = w0 * p0[1] + w1 * p1[1] + w2 * p2[1];
    if (y <= feetY + 0.35) {
      if (below === null || y > below) below = y;
    } else if (above === null || y < above) {
      above = y;
    }
  }
  return { below, above };
}

function roofHeightAt(b, x, z) {
  let best = null;
  for (const tri of b.roof || []) {
    const [p0, p1, p2] = tri;
    // barycentric in the XZ plane
    const d = (p1[2] - p2[2]) * (p0[0] - p2[0]) + (p2[0] - p1[0]) * (p0[2] - p2[2]);
    if (Math.abs(d) < 1e-9) continue;
    const w0 = ((p1[2] - p2[2]) * (x - p2[0]) + (p2[0] - p1[0]) * (z - p2[2])) / d;
    const w1 = ((p2[2] - p0[2]) * (x - p2[0]) + (p0[0] - p2[0]) * (z - p2[2])) / d;
    const w2 = 1 - w0 - w1;
    if (w0 < -0.02 || w1 < -0.02 || w2 < -0.02) continue;
    const y = w0 * p0[1] + w1 * p1[1] + w2 * p2[1];
    if (best === null || y > best) best = y;
  }
  return best;
}

// crosses(): the side samples either side of a crossed edge (m)
const CROSS_EPS = 0.02;

// Is (x, z) inside b's solid at feet yb? resolve()'s per-building rule at a
// point: in the footprint, not in a hole, and not where the feet stand on
// the roof (the roof tri there, else the apex when b has no roof mesh).
// The height band (eaves, footing, passage) is the caller's.
function solidIn(b, x, z, yb) {
  if (!inAnyRing(x, z, b.fp) || (b.holes && inAnyRing(x, z, b.holes))) return false;
  const roofY = roofHeightAt(b, x, z);
  const standY = roofY !== null ? roofY : ((b.roof && b.roof.length) ? null : b.y1);
  return !(standY !== null && yb >= standY - 0.35);
}

// Drop degenerate vertices: coincident points, and SPIKE tips where the ring
// doubles back on itself (out-and-straight-back-in), which the bake's
// buffer(+0.05).buffer(-0.05).simplify(0.06) leaves behind on ~1 % of
// footprints. A needle has interior on both sides of a zero-width sliver, so
// no exit direction can ever leave it and a body beside one is unresolvable.
function cleanRing(ring) {
  const r = [];
  for (const p of ring) {
    const q = r[r.length - 1];
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-6) r.push(p);
  }
  while (r.length > 3 && Math.hypot(r[0][0] - r[r.length - 1][0],
    r[0][1] - r[r.length - 1][1]) < 1e-6) r.pop();
  if (r.length < 3) return r;
  const cos = (ax, az, bx, bz) => {
    const la = Math.hypot(ax, az), lb = Math.hypot(bx, bz);
    return (la < 1e-9 || lb < 1e-9) ? 0 : (ax * bx + az * bz) / (la * lb);
  };
  for (let guard = 0; guard < 2 * r.length && r.length > 3; guard++) {
    let cut = -1, n = 1;
    for (let i = 0; i < r.length; i++) {
      const p = r[(i - 1 + r.length) % r.length], q = r[i];
      const s = r[(i + 1) % r.length], t = r[(i + 2) % r.length];
      // sharp spike: one vertex the ring doubles straight back from
      if (cos(q[0] - p[0], q[1] - p[1], s[0] - q[0], s[1] - q[1]) < -0.99) {
        cut = i; n = 1; break;
      }
      // BLUNT-tipped needle: a very short edge joining two long antiparallel
      // edges — a zero-width slit. simplify(0.06) leaves these behind, and a
      // body beside one can never be freed (interior on both sides).
      if (Math.hypot(s[0] - q[0], s[1] - q[1]) < 0.3
        && cos(q[0] - p[0], q[1] - p[1], t[0] - s[0], t[1] - s[1]) < -0.9) {
        cut = i; n = 2; break;
      }
    }
    if (cut < 0) break;
    for (let k = 0; k < n; k++) r.splice(cut % r.length, 1);
  }
  return r;
}

function pointInRing(x, z, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i], pj = ring[j];
    const xi = pi[0], zi = pi[1], xj = pj[0], zj = pj[1];
    if ((zi > z) !== (zj > z)
      && x < ((xj - xi) * (z - zi)) / (zj - zi + 1e-12) + xi) inside = !inside;
  }
  return inside;
}

// rings.some((ring) => pointInRing(x, z, ring)) without a closure per call
function inAnyRing(x, z, rings) {
  for (let i = 0; i < rings.length; i++) if (pointInRing(x, z, rings[i])) return true;
  return false;
}

function distToRing(x, z, ring) {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    best = Math.min(best, distToSeg(x, z, ring[j], ring[i]));
  }
  return best;
}

function distToSeg(x, z, a, b) {
  const dx = b[0] - a[0], dz = b[1] - a[1];
  const t = Math.max(0, Math.min(1,
    ((x - a[0]) * dx + (z - a[1]) * dz) / (dx * dx + dz * dz + 1e-12)));
  return Math.hypot(x - (a[0] + dx * t), z - (a[1] + dz * t));
}

// Capsule (circle of radius r) vs ONE footprint ring. Returns the minimal
// displacement that separates the two, or null when they don't overlap.
//
// Containment is volumetric: the old test was point-in-polygon of the body
// CENTRE, so a body could stand with half of itself merged into a facade —
// permanently, because the centre never crossed. `depth` already includes the
// radius, so applying the displacement lands the body exactly tangent to the
// wall and no further.
// `blocked(cx, cz)` (optional) reports that a resolved body centre would still
// be inside some other solid — such an exit is rejected and the next-shortest
// one is tried, so the body leaves the whole union rather than hopping between
// its parts.
// `inside` is the BUILDING-level solidity of (x,z), passed in rather than
// re-derived per ring. That is what makes courtyards work: for a hole ring the
// body sits geometrically OUTSIDE the ring while being inside the solid, and
// the exit then correctly points into the light-well.
function inOuterOf(b, x, z) {
  return b.fp.length ? b.fp.some((ring) => pointInRing(x, z, ring)) : true;
}

function ringPush(x, z, ring, r, blocked, inside) {
  const cand = [];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j], b = ring[i];
    const dx = b[0] - a[0], dz = b[1] - a[1];
    const t = Math.max(0, Math.min(1,
      ((x - a[0]) * dx + (z - a[1]) * dz) / (dx * dx + dz * dz + 1e-12)));
    const qx = a[0] + dx * t, qz = a[1] + dz * t;
    cand.push({ d: Math.hypot(x - qx, z - qz), qx, qz, ax: a[0], az: a[1], bx: b[0], bz: b[1] });
  }
  if (!cand.length) return null;
  cand.sort((p, q) => p.d - q.d);
  if (!inside && cand[0].d >= r) return null;      // capsule clear of this ring
  // shortest exit first, so a body inside leaves by the nearest wall that
  // actually reaches open space
  let fallback = null;
  for (const c of cand) {
    const depth = inside ? c.d + r : r - c.d;
    if (depth <= 1e-4) continue;
    // outward: inside -> toward the boundary; outside -> away from it
    let ox = inside ? c.qx - x : x - c.qx;
    let oz = inside ? c.qz - z : z - c.qz;
    let l = Math.hypot(ox, oz);
    if (l < 1e-6) {
      // dead on the boundary polyline: use the segment perpendicular, oriented
      // away from the interior. (A fixed +X fallback pushed every east-facing
      // facade straight back into its own wall.)
      ox = -(c.bz - c.az); oz = c.bx - c.ax;
      l = Math.hypot(ox, oz) || 1;
      ox /= l; oz /= l;
      if (pointInRing(x + ox * 0.01, z + oz * 0.01, ring)) { ox = -ox; oz = -oz; }
      l = 1;
    }
    const out = { dx: (ox / l) * depth, dz: (oz / l) * depth, depth };
    if (!fallback) fallback = out;
    if (!blocked || !blocked(x + out.dx, z + out.dz)) return out;
  }
  return fallback;
}

module.exports = { BuildingCollision };
